/**
 * Composizione kit stile "Nuovo ordine" (feedback stakeholder 2026-07-24):
 * scegli Negozio + Postazione, seleziona gli oggetti dal catalogo ordini
 * (con quantità) e conferma: gli strumenti vengono creati subito in Magazzino
 * raggruppati in un folder kit. preset="base" precompila il Kit di base.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Minus, Plus, Search, X } from 'lucide-react';
import api, { kitsApi, stationsApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from './ui/dialog';
import { toast } from 'sonner';

const eur = (v) => `€ ${(Number(v) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`;

export const KitComposeDialog = ({ preset, stores, onClose, onDone }) => {
  const isBase = preset === 'base';
  const [storeId, setStoreId] = useState('');
  const [stationId, setStationId] = useState('');
  const [stations, setStations] = useState([]);
  const [newStationName, setNewStationName] = useState('');
  const [creatingStation, setCreatingStation] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [catalogError, setCatalogError] = useState(false);
  const [search, setSearch] = useState('');
  const [qty, setQty] = useState({});           // product_id -> qty
  const [rawItems, setRawItems] = useState([]); // righe Kit di base
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/orders/catalog').then(r => setCatalog(r.data || [])).catch(() => setCatalogError(true));
    if (isBase) {
      kitsApi.baseTemplate().then(r => setRawItems((r.data?.items || []).map(it => ({ ...it }))))
        .catch(() => toast.error('Impossibile caricare il Kit di base'));
    }
  }, [isBase]);

  useEffect(() => {
    setStationId('');
    if (storeId) {
      stationsApi.list(storeId).then(r => setStations(r.data?.stations || [])).catch(() => setStations([]));
    } else {
      setStations([]);
    }
  }, [storeId]);

  const realStores = useMemo(() => (stores || []).filter(s => !s.is_special), [stores]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const map = new Map();
    catalog
      .filter(p => !q || `${p.name} ${p.code || ''}`.toLowerCase().includes(q))
      .forEach(p => {
        if (!map.has(p.category)) map.set(p.category, []);
        map.get(p.category).push(p);
      });
    return [...map.entries()];
  }, [catalog, search]);

  const chosen = useMemo(
    () => catalog.filter(p => (qty[p.product_id] || 0) > 0),
    [catalog, qty]);
  const totalCount = chosen.reduce((s, p) => s + qty[p.product_id], 0)
    + rawItems.reduce((s, r) => s + (r.qty || 1), 0);
  const totalValue = chosen.reduce((s, p) => s + (Number(p.price) || 0) * qty[p.product_id], 0);

  const setProductQty = (id, v) => setQty(prev => {
    const next = { ...prev };
    if (v <= 0) delete next[id]; else next[id] = Math.min(99, v);
    return next;
  });
  const setRawQty = (i, v) => setRawItems(rs => rs.map((r, j) => j === i ? { ...r, qty: Math.max(1, Math.min(99, v)) } : r));

  async function createStation() {
    if (!newStationName.trim()) return;
    setCreatingStation(true);
    try {
      const r = await stationsApi.create(storeId, { name: newStationName.trim() });
      const st = r.data;
      setStations(prev => [...prev, st]);
      setStationId(st.station_id);
      setNewStationName('');
      toast.success(`Postazione "${st.name}" creata`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore creazione postazione');
    } finally {
      setCreatingStation(false);
    }
  }

  async function submit() {
    if (!storeId) { toast.error('Seleziona il negozio'); return; }
    if (totalCount === 0) { toast.error('Aggiungi almeno un oggetto al kit'); return; }
    setSaving(true);
    try {
      const r = await kitsApi.compose({
        store_id: storeId,
        station_id: stationId || null,
        products: chosen.map(p => ({ product_id: p.product_id, qty: qty[p.product_id] })),
        raw_items: rawItems,
      });
      toast.success(`${r.data.created} strumenti creati in ${r.data.store_name}${r.data.station_name ? ` – ${r.data.station_name}` : ''}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nella creazione del kit');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col" data-testid="kit-compose-dialog">
        <DialogHeader>
          <DialogTitle>{isBase ? 'Kit di base' : 'Nuovo kit'}</DialogTitle>
          <DialogDescription>
            {isBase
              ? 'Dotazione standard precaricata: scegli negozio e postazione, aggiungi altro dal catalogo se serve.'
              : 'Scegli negozio e postazione, poi seleziona gli oggetti dal catalogo. Gli strumenti vengono creati subito in magazzino.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Negozio *</Label>
            <Select value={storeId} onValueChange={setStoreId}>
              <SelectTrigger data-testid="kit-compose-store"><SelectValue placeholder="— seleziona —" /></SelectTrigger>
              <SelectContent>
                {realStores.map(s => <SelectItem key={s.store_id} value={s.store_id}>{s.display_name || s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Postazione</Label>
            <Select value={stationId || 'none'} onValueChange={(v) => setStationId(v === 'none' ? '' : v)} disabled={!storeId}>
              <SelectTrigger data-testid="kit-compose-station"><SelectValue placeholder="— nessuna —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— nessuna (strumenti liberi) —</SelectItem>
                {stations.map(st => <SelectItem key={st.station_id} value={st.station_id}>{st.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {storeId && (
              <div className="flex gap-1.5 mt-1.5">
                <Input value={newStationName} onChange={e => setNewStationName(e.target.value)}
                       placeholder={stations.length === 0 ? 'Nessuna postazione — creane una…' : '+ Nuova postazione…'}
                       className="h-8 text-xs" data-testid="kit-compose-new-station" />
                <Button type="button" size="sm" variant="outline" onClick={createStation}
                        disabled={creatingStation || !newStationName.trim()}
                        className="h-8 text-xs rounded-md shrink-0" data-testid="kit-compose-create-station">
                  {creatingStation ? '…' : 'Crea'}
                </Button>
              </div>
            )}
          </div>
        </div>

        {rawItems.length > 0 && (
          <div className="border border-slate-200 rounded-md overflow-y-auto max-h-44 shrink-0" data-testid="kit-compose-preset-items">
            <p className="px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50 sticky top-0">Dotazione base</p>
            <div className="divide-y divide-slate-100">
            {rawItems.map((r, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                <span className="flex-1 min-w-0 truncate">{r.model || r.category_name}
                  <span className="text-slate-400 text-xs ml-1.5">{r.category_name}</span>
                </span>
                <div className="flex items-center gap-1.5">
                  <button type="button" onClick={() => setRawQty(i, (r.qty || 1) - 1)} className="p-1 border border-slate-200 rounded hover:bg-slate-50" data-testid={`kit-raw-minus-${i}`}><Minus className="w-3 h-3" /></button>
                  <span className="w-6 text-center text-sm font-semibold">{r.qty || 1}</span>
                  <button type="button" onClick={() => setRawQty(i, (r.qty || 1) + 1)} className="p-1 border border-slate-200 rounded hover:bg-slate-50" data-testid={`kit-raw-plus-${i}`}><Plus className="w-3 h-3" /></button>
                </div>
                <button type="button" onClick={() => setRawItems(rs => rs.filter((_, j) => j !== i))}
                        className="p-1 text-slate-400 hover:text-red-600" data-testid={`kit-raw-remove-${i}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            </div>
          </div>
        )}

        <div className="flex-1 min-h-0 flex flex-col gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca nel catalogo…"
                   className="pl-9" data-testid="kit-compose-search" />
          </div>
          <div className="flex-1 overflow-y-auto border border-slate-200 rounded-md min-h-[140px] max-h-[240px]" data-testid="kit-compose-catalog">
            {catalogError ? (
              <p className="text-sm text-slate-500 p-4">Catalogo ordini non disponibile.</p>
            ) : grouped.length === 0 ? (
              <p className="text-sm text-slate-500 p-4">Nessun prodotto trovato.</p>
            ) : grouped.map(([cat, prods]) => (
              <div key={cat}>
                <p className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50 sticky top-0">{cat}</p>
                {prods.map(p => {
                  const q = qty[p.product_id] || 0;
                  return (
                    <div key={p.product_id}
                         className={`flex items-center gap-2 px-3 py-1.5 text-sm border-b border-slate-50 ${q > 0 ? 'bg-emerald-50/60' : ''}`}
                         data-testid={`kit-compose-product-${p.product_id}`}>
                      <span className="flex-1 min-w-0 truncate">{p.name}</span>
                      <span className="text-xs text-slate-400 shrink-0">{p.price != null ? eur(p.price) : '—'}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button type="button" onClick={() => setProductQty(p.product_id, q - 1)}
                                className="p-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-30"
                                disabled={q === 0} data-testid={`kit-compose-minus-${p.product_id}`}>
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-6 text-center text-sm font-semibold">{q}</span>
                        <button type="button" onClick={() => setProductQty(p.product_id, q + 1)}
                                className="p-1 border border-slate-200 rounded hover:bg-slate-50"
                                data-testid={`kit-compose-plus-${p.product_id}`}>
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="items-center gap-3 sm:justify-between">
          <span className="text-sm text-slate-600" data-testid="kit-compose-total">
            <span className="font-bold">{totalCount}</span> strumenti{totalValue > 0 && <> · {eur(totalValue)}</>}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Annulla</Button>
            <Button onClick={submit} disabled={saving || !storeId || totalCount === 0} data-testid="kit-compose-submit">
              {saving ? 'Creazione…' : `Crea ${totalCount} strumenti`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
